import { useCallback } from 'react';
import { isDesktop } from 'react-device-detect';
import { toast } from 'react-toastify';

import Cookies from 'js-cookie';

import { WalletInterface, WalletNetwork, WalletType } from '@api';

import { storageKeys } from '@shared/constants';
import clearSavedConnectionDetails from '@shared/helpers/web3/clearSavedConnectionDetails';
import { formatWalletTypeCase } from '@shared/helpers/web3/formatWalletTypeCase';
import useMagicIdWallet from '@shared/hooks/useMagicIdWallet';

const useMagicIdUserWalletInfo = (targetNetwork: WalletNetwork = 'EVM') => {
  const { userWallet } = useMagicIdWallet();

  const getUserWalletType = useCallback((): {
    walletType: WalletType;
    userWallet: WalletInterface;
  } => {
    if (!userWallet) {
      throw new Error(`Please add ${targetNetwork} wallet to your Magic ID`);
    }

    let savedConnectionType = Cookies.get(storageKeys.MSQ_CONNECTED_WALLET_TYPE);

    if (savedConnectionType === 'Phantom' && targetNetwork !== 'SOLANA') {
      clearSavedConnectionDetails();
      savedConnectionType = undefined;
    }

    const formattedSavedWalletType = formatWalletTypeCase(savedConnectionType);

    // TODO: Come up with a better solution to remove toast side effect and make it pure function
    if (!isDesktop && formattedSavedWalletType === 'EVM') {
      toast.info('EVM wallets is only available by WalletConnect on Mobile.');
    }

    if (!isDesktop && formattedSavedWalletType === 'Phantom') {
      throw new Error('Phantom wallet is not available on Mobile.');
    }

    let walletType = !isDesktop
      ? 'WalletConnect'
      : formattedSavedWalletType || userWallet.attributes.wallet || 'WalletConnect2';

    if (walletType === 'EVM') {
      walletType = 'WalletConnect2';
    }

    if (formattedSavedWalletType === 'WalletConnect2') {
      walletType = formattedSavedWalletType;
    }

    return {
      walletType,
      userWallet,
    };
  }, [userWallet, targetNetwork]);
  const getUserWalletAddress = useCallback((): {
    address: string;
    userWallet: WalletInterface;
  } => {
    if (!userWallet) {
      throw new Error(`Please add ${targetNetwork} wallet to your Magic ID`);
    }

    return {
      address: userWallet.attributes.address,
      userWallet,
    };
  }, [userWallet, targetNetwork]);

  return {
    getUserWalletType,
    getUserWalletAddress,
  };
};

export default useMagicIdUserWalletInfo;
